import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Spinner,
} from "react-bootstrap";
import axios from "axios";
// import ThankYouModal from "./ThankYouModal"; // Commented out as per original code context, though the component is defined in the file
import illustration from "../images/illustration.png";
import handsImage from "../images/thum.png";

// Import all necessary MUI components
import {
  Autocomplete,
  TextField,
  Grid,
  Link,
  Typography,
  Box,
  styled,
} from "@mui/material";
import Popper from "@mui/material/Popper";
import StudyAbroadStats from "./StudyAbroadStats";
// --- REFRACTORED TO USE ENVIRONMENT VARIABLES ---
const API_BASE_URL = process.env.REACT_APP_AVANSE_API_BASE_URL;
const AUTH_TOKEN = process.env.REACT_APP_AVANSE_AUTH_TOKEN;
const PRODUCT_ID = process.env.REACT_APP_AVANSE_PRODUCT_ID;
const APP_ID = process.env.REACT_APP_AVANSE_APP_ID;
const SOURCE_ID = process.env.REACT_APP_AVANSE_SOURCE_ID;
const PARTNER_FIRM_ID = process.env.REACT_APP_AVANSE_PARTNER_FIRM_ID;
const PARTNER_FIRM_CODE = process.env.REACT_APP_AVANSE_PARTNER_FIRM_CODE;
const BRE_API_URL = process.env.REACT_APP_BRE_API_URL;
const BRE_AUTH_TOKEN = process.env.REACT_APP_BRE_AUTH_TOKEN;

const CustomPopper = styled(Popper)(({ theme }) => ({
  "& .MuiAutocomplete-noOptions": {
    color: "#000",
    fontFamily: "Jost, sans-serif",
    padding: "8px 6px",
    borderRadius: "4px",
    fontWeight: "500",
    fontSize: "12px",
  },
  "& .MuiAutocomplete-listbox li": {
    color: "#000",
    fontWeight: "500",
    fontSize: "10px",
    padding: "5px 6px",
    fontFamily: "Jost, sans-serif",
    whiteSpace: "normal", // ✅ Allows wrapping
    wordBreak: "break-word", // ✅ Breaks long words
    overflowWrap: "anywhere",
    "&:hover": {
      backgroundColor: "#c5cae9",
    },
  },
}));
// Define API headers using environment variables
const headers = {
  "Content-Type": "application/json",
  Language: "en-US",
  Authorization: `Bearer ${AUTH_TOKEN}`,
  "product-id": PRODUCT_ID,
  "customer-id": "NA",
  "source-id": SOURCE_ID,
  "app-id": APP_ID,
};

// Define the ThankYouModal component
const ThankYouModalComponent = ({ show, onHide, breData }) => {
  const years = breData?.Max_Tenure ? Math.floor(breData.Max_Tenure / 12) : 0;
  const months = breData?.Max_Tenure ? breData.Max_Tenure % 12 : 0;

  return (
    <div className={`thank-you-modal-backdrop ${show ? "show" : ""}`}>
      <div className="thank-you-modal-dialog">
        <div className="thank-you-modal-content">
          <div className="modal-body p-0">
            <Button
              type="button"
              className="btn-close thank-you-modal-close"
              onClick={onHide}
              aria-label="Close"
            ></Button>

            {!breData?.Max_Loan_Amount || !breData?.Max_Tenure ? (
              // Fallback "Thank You" Message
              <div className="thank-you-modal-body fallback-message">
                <div className="modal-text-column">
                  <h2 className="modal-title">
                    Thank You!
                    <img src={handsImage} alt="Thumbs up" />
                  </h2>
                  <h3 className="modal-subtitle">
                    We've received your details.
                  </h3>
                  <p className="modal-description">
                    One of our expert loan advisors will review your information
                    and get in touch with you shortly to guide you through the
                    next steps.
                  </p>
                </div>
                <div className="modal-image-column">
                  <img
                    src={illustration}
                    alt="Contact us"
                    className="modal-illustration"
                  />
                </div>
              </div>
            ) : (
              // Success "Congratulations" Message
              <div className="thank-you-modal-body success-message">
                <div className="modal-text-column">
                  <h4 className="modal-congrats">
                    <span style={{ position: "relative", top: "-9px" }}>
                      Congratulations
                    </span>
                    <img src={handsImage} alt="Thumbs up" />
                  </h4>
                  <h1 className="modal-title">Great news!</h1>
                  <p className="modal-description">
                    You're eligible for a student loan of <br />
                    <span className="highlight-text">
                      Rs.{" "}
                      {new Intl.NumberFormat("en-IN").format(
                        breData.Max_Loan_Amount
                      )}
                      *
                    </span>
                    <br /> with an easy repayment term of <br />
                    <span className="highlight-text">
                      {years} years {months} months*
                    </span>
                  </p>
                </div>
                <div className="modal-image-column">
                  <img
                    src={illustration}
                    alt="Student travelling abroad"
                    className="modal-illustration"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const PREFERRED_COUNTRIES_OPTIONS = [
  { label: "AUSTRALIA", countryId: "AU" },
  { label: "CANADA", countryId: "CA" },
  { label: "IRELAND", countryId: "IR" },
  { label: "NEW ZEALAND", countryId: "NZ" },
  { label: "UNITED KINGDOM", countryId: "UK" },
  { label: "UNITED STATES OF AMERICA", countryId: "US" },
  { label: "OTHER", value: "Other", countryId: "OTHER" },
];

const LoanForm = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    mobile: "",
    email: "",
    pincode: "",
    admissionStatus: "",
    idpRepresentative: "",
    country: null,
    preferredCountry: null, // New state for the preferred country dropdown
    university: null,
    institute: null,
    course: null,
    stream: "",
    courseCategory: "",
    loanAmount: "",
    marks12th: "",
    graduationGPA: "",
    greScore: "",
    gmatScore: "",
  });

  const [idpRepresentatives, setIdpRepresentatives] = useState([]);
  const [countries, setCountries] = useState([]);

  // States for Autocomplete and manual entry
  const [universities, setUniversities] = useState([]);
  const [useManualUniversity, setUseManualUniversity] = useState(false);
  const [institutes, setInstitutes] = useState([]);
  const [useManualInstitute, setUseManualInstitute] = useState(false);
  const [courses, setCourses] = useState([]);
  const [useManualCourse, setUseManualCourse] = useState(false);

  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [apiMessage, setApiMessage] = useState({ type: "", message: "" });
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [breData, setBreData] = useState(null);
  const inputRefs = useRef(new Map());

  const handleWheel = (e) => {
    // You can put any logic here, including preventDefault()
    e.preventDefault();
  };
  const setInputRef = (element) => {
    if (element) {
      // Add the element to our Map using its name as a key for easy lookup
      inputRefs.current.set(element.name, element);

      // Immediately attach the event listener to this specific element
      element.addEventListener("wheel", handleWheel, { passive: false });
    }
  };
  useEffect(() => {
    // Capture a snapshot of the current ref value when the effect is created
    const currentRefs = inputRefs.current;

    // This effect is now ONLY for cleanup.
    return () => {
      // Use the captured variable, which holds a reference to the same Map instance
      currentRefs.forEach((element) => {
        if (element) {
          element.removeEventListener("wheel", handleWheel);
        }
      });
      // Clearing the map here is also a good practice
      currentRefs.clear();
    };
  }, []);

  // Fetch static data on component mount
  useEffect(() => {
    const fetchStaticData = async () => {
      try {
        // Fetch countries
        const countryRes = await axios.post(
          `${API_BASE_URL}/getAll/Countries`,
          {},
          { headers }
        );
        if (countryRes.data?.dtoList) {
          const countryOptions = countryRes.data.dtoList
            .filter((c) => c.staus !== false)
            .map((c) => ({ label: c.countryName, value: c.countryId }));
          setCountries(countryOptions);
        }

        // Fetch IDP Branchs
        const repRes = await axios.post(
          `${API_BASE_URL}/filteredlistingPartnerUser`,
          { partnerFirmId: PARTNER_FIRM_ID, isPrimary: "0", roleType: "PLMU" },
          { headers }
        );
        if (repRes.data?.dtoList) {
          if (repRes.data?.dtoList) {
            setIdpRepresentatives(repRes.data.dtoList);
          }
        }
      } catch (error) {
        setApiMessage({
          type: "danger",
          message: `Submission failed: ${error}`,
        });

        console.error("Failed to fetch static data:", error);
      }
    };
    fetchStaticData();
  }, []);

  const fetchDropdownData = useCallback(
    async (
      type,
      query,
      countryId,
      universityId,
      instituteId,
      undergraduateFlag
    ) => {
      if (query.length < 4) return [];

      let endpoint = "";
      let params = {};
      switch (type) {
        case "university":
          endpoint = "search/universityByCountryId";
          params = { universityName: query, countryId };
          break;
        case "institute":
          endpoint = "search/instituteByCountryIdAndUniversityId";
          params = { instituteName: query, universityId, countryId };
          break;
        case "course":
          endpoint = "search/Course";
          params = { courseName: query, instituteId, undergraduateFlag };
          break;
        default:
          return [];
      }

      try {
        const response = await axios.post(
          `${API_BASE_URL}/${endpoint}`,
          {},
          { headers, params }
        );
        return (
          response.data?.dtoList?.map((item) => {
            switch (type) {
              case "university":
                return {
                  label: item.universityName,
                  value: item.universityId,
                  code: item.universityCode,
                };
              case "institute":
                return {
                  label: item.instituteName,
                  value: item.instituteId,
                  code: item.instituteCode,
                };
              case "course":
                return {
                  label: item.courseName,
                  value: item.courseId,
                  code: item.courseCode,
                };
              default:
                return {};
            }
          }) || []
        );
      } catch (error) {
        setApiMessage({
          type: "danger",
          message: `Submission failed: ${error}`,
        });

        console.error(`Failed to fetch ${type}:`, error);
        return [];
      }
    },
    []
  );

  // New handler for the preferred country dropdown
  const handlePreferredCountryChange = (e, newValue) => {
    setFormData((prev) => ({ ...prev, preferredCountry: newValue }));

    // Reset country, university, institute, and course when preferred country changes
    setFormData((prev) => ({
      ...prev,
      country: null, // Reset the main country dropdown value
      university: null,
      institute: null,
      course: null,
    }));
    setUniversities([]);
    setInstitutes([]);
    setCourses([]);
    setUseManualUniversity(false);
    setUseManualInstitute(false);
    setUseManualCourse(false);
    setErrors((prevErrors) => ({ ...prevErrors, preferredCountry: "" }));

    // If a specific country is selected, set it as the main country
    if (newValue && newValue.value !== "Other") {
      setFormData((prev) => ({
        ...prev,
        // Set the main country with the value from the preferred country list
        country: { label: newValue.label, value: newValue.countryId },
      }));
    }
    // If 'Other' is selected, formData.country remains null, and the full country list dropdown will appear.
  };

  const handleCountryChange = (e, newValue) => {
    setFormData((prev) => ({ ...prev, country: newValue }));
    setFormData((prev) => ({
      ...prev,
      university: null,
      institute: null,
      course: null,
    }));
    setUniversities([]);
    setInstitutes([]);
    setCourses([]);
    setUseManualUniversity(false);
    setUseManualInstitute(false);
    setUseManualCourse(false);
    setErrors((prevErrors) => ({ ...prevErrors, country: "" }));
  };

  const handleUniversityChange = (e, newValue) => {
    setFormData((prev) => ({ ...prev, university: newValue }));
    setFormData((prev) => ({ ...prev, institute: null, course: null }));
    setUniversities([]);
    setInstitutes([]);
    setCourses([]);
    setUseManualInstitute(false);
    setUseManualCourse(false);
    setErrors((prevErrors) => ({ ...prevErrors, university: "" }));
  };

  const handleInstituteChange = (e, newValue) => {
    setFormData((prev) => ({ ...prev, institute: newValue }));
    setFormData((prev) => ({ ...prev, course: null }));
    setCourses([]);
    setUseManualCourse(false);
    setErrors((prevErrors) => ({ ...prevErrors, institute: "" }));
  };

  const handleCourseChange = (e, newValue) => {
    setFormData((prev) => ({ ...prev, course: newValue }));
    setErrors((prevErrors) => ({ ...prevErrors, course: "" }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log("name: " + name);
    console.log("value: " + value);

    // ✅ START: Logic to reset fields on Admission Status change
    if (name === "admissionStatus") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        // Reset conditional fields that change based on Admission Status
        preferredCountry: null,
        country: null,
        university: null,
        institute: null,
        course: null,
        marks12th: "",
        graduationGPA: "",
        greScore: "",
        gmatScore: "",
      }));
      // Reset autocomplete suggestions and manual entry flags
      setUniversities([]);
      setInstitutes([]);
      setCourses([]);
      setUseManualUniversity(false);
      setUseManualInstitute(false);
      setUseManualCourse(false);
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    // ✅ END: Logic to reset fields on Admission Status change

    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.firstName.trim())
      newErrors.firstName = "First Name is required.";
    if (!formData.lastName.trim())
      newErrors.lastName = "Last Name is required.";
    if (!formData.mobile.trim() || !/^[6-9]\d{9}$/.test(formData.mobile.trim()))
      newErrors.mobile = "Valid 10-digit Mobile No. is required.";
    if (
      !formData.email.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())
    )
      newErrors.email = "Valid Email ID is required.";
    if (
      !formData.pincode.trim() ||
      !/^[1-9][0-9]{5}$/.test(formData.pincode.trim())
    )
      newErrors.pincode = "Valid 6-digit Pincode is required.";
    if (!formData?.counsellor?.trim())
      newErrors.counsellor = "IDP counsellor is required.";
    if (!formData.admissionStatus)
      newErrors.admissionStatus = "Admission Status is required.";
    if (!formData.idpRepresentative)
      newErrors.idpRepresentative = "IDP Branch is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep1()) {
      setCurrentStep(2);
      setApiMessage({ type: "", message: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    // Determine the actual country object being used for submission and validation
    // The country for submission is either the selected preferred country (if not 'Other') or the country from the full list.
    const finalCountry =
      formData.preferredCountry?.value !== "Other"
        ? {
            value: formData.preferredCountry?.countryId,
            label: formData.preferredCountry?.label,
          }
        : formData.country;

    // Step 2 validation
    if (!formData.stream) newErrors.stream = "Stream is required.";
    if (!formData.courseCategory)
      newErrors.courseCategory = "Course Category is required.";
    if (!formData.loanAmount) newErrors.loanAmount = "Loan Amount is required.";
    if (!formData.preferredCountry)
      newErrors.preferredCountry = "Preferred Study Place is required."; // Validate new preferred dropdown
    if (formData.preferredCountry?.value === "Other" && !formData.country)
      newErrors.country = "Country of Study is required."; // Validate full country dropdown if 'Other' is selected

    let universityName = useManualUniversity
      ? formData.university
      : formData.university?.label || "";
    let instituteName = useManualInstitute
      ? formData.institute
      : formData.institute?.label || "";
    let courseName = useManualCourse
      ? formData.course
      : formData.course?.label || "";

    if (
      formData.admissionStatus === "Confirmed" ||
      formData.admissionStatus === "Applied"
    ) {
      if (!universityName)
        newErrors.university = "University Name is required.";
      if (!instituteName) newErrors.institute = "Institute Name is required.";
      if (!courseName) newErrors.course = "Course Name is required.";
    } else if (formData.admissionStatus === "Undecided") {
      const is12thAndGpaValid =
        parseFloat(formData.marks12th) >= 0 &&
        parseFloat(formData.marks12th) <= 100 &&
        parseFloat(formData.graduationGPA) >= 0 &&
        parseFloat(formData.graduationGPA) <= 100;
      const isGreValid =
        !isNaN(parseFloat(formData.greScore)) &&
        parseFloat(formData.greScore) >= 0;
      const isGmatValid =
        !isNaN(parseFloat(formData.gmatScore)) &&
        parseFloat(formData.gmatScore) >= 0;

      if (!is12thAndGpaValid && !isGreValid && !isGmatValid) {
        newErrors.academicScores =
          "Please provide 12th Marks & Graduation GPA or GRE score or GMAT score.";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    setApiMessage({ type: "", message: "" });
    const getLeadCourseTypeName = (category) => {
      if (category === "Graduation") return "Under Graduate";
      if (category === "Post Graduation") return "Post Graduate";
      return "";
    };

    try {
      const crmPayload = {
        leadFirstName: formData.firstName,
        partnerNotes: formData.counsellor,
        leadLastName: formData.lastName,
        leadMobileNo: formData.mobile,
        leadEmailAddr: formData.email,
        leadPincode: formData.pincode,
        amountRequested: formData.loanAmount,
        admissionStatus: formData.admissionStatus,
        countryOfStudyId: finalCountry?.value, // Use finalCountry for submission
        universityId: !useManualUniversity ? formData.university?.code : "",
        universityNotInList: useManualUniversity,
        otherUniversityName: useManualUniversity ? formData.university : "",
        instituteId: !useManualInstitute ? formData.institute?.code : "",
        instituteNotInList: useManualInstitute,
        otherInstituteName: useManualInstitute ? formData.institute : "",
        courseId: !useManualCourse ? formData.course?.code : "",
        courseNotInList: useManualCourse,
        otherCourseName: useManualCourse ? formData.course : "",
        stemFlag: formData.stream === "STEM" ? true : false,
        leadType: "Partner",
        partnerDisplayCode: PARTNER_FIRM_CODE,
        leadCourseTypeName: getLeadCourseTypeName(formData.courseCategory),
        userId: formData.idpRepresentative,
        partnerFirmId: PARTNER_FIRM_ID,
        leadDocumentIdentifier: "IDPUTMLink",
        leadSource: "Partner",
        leadSystem: "API",
        partnerLead: true,
        qualifiedPartner: true,
      };
      // const response = await fetch(
      //         https://api-uthirdparty.avanse.com/edge-service/partnersales-service/partnerLeadDedupeValidation?partnerFirmId=${PARTNER_FIRM_ID}&key=PAN&val=${value},
      //         {
      //           method: "POST",
      //         }
      //       );
      const crmResponse = await axios.post(
        `${API_BASE_URL}/create/Lead/Crm`,
        crmPayload,
        { headers }
      );

      let breResponseData = {};
      const getCourseCategoryForBRE = (category) => {
        if (category === "Graduation") return "Graduation";
        if (category === "Post Graduation") return "Post Graduation";
        return "";
      };

      const getStreamCategoryForBRE = (stream) => {
        if (stream === "STEM") return "STEM";
        if (stream === "MANAGEMENT") return "MANAGEMENT";
        if (stream === "OTHERS") return "OTHERS";
        return "";
      };

      if (crmResponse.data?.leadId) {
        const brePayload = {
          values: {
            input: {
              Unique_Lead_ID: crmResponse.data.leadId,
              oApplicant: {
                oApplName: {
                  sFirstName: formData.firstName,
                  sLastName: formData.lastName,
                },
                aAcademics: [
                  {
                    bachelor_score: formData.graduationGPA || "0",
                    hsc_score: formData.marks12th || "0",
                    other_scores: [
                      {
                        exam_type: "gre",
                        exam_score: formData.greScore || "0",
                      },
                      {
                        exam_type: "gmat",
                        exam_score: formData.gmatScore || "0",
                      },
                    ],
                  },
                ],
                aCourseDetails: [
                  {
                    institute_code: formData.institute?.code || "0",
                    course_code: formData.course?.code || "0",
                    university_campus_id: formData.university?.code || "0",
                    university_country_id: finalCountry?.value || "", // Use finalCountry for BRE
                    category: getStreamCategoryForBRE(formData.stream),
                    course_category: getCourseCategoryForBRE(
                      formData.courseCategory
                    ),
                    admission_status: formData.admissionStatus,
                  },
                ],
              },
            },
          },
        };
        const breResponse = await axios.post(BRE_API_URL, brePayload, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${BRE_AUTH_TOKEN}`,
          },
        });
        breResponseData = breResponse.data;
      } else {
        console.error("CRM submission failed, skipping BRE API call.");
      }

      setBreData(breResponseData);
      setShowThankYouModal(true);
    } catch (error) {
      console.error("Error during form submission:", error);
      const errorMessage =
        axios.isAxiosError(error) && error.response?.data?.error?.message
          ? error.response.data.error.message
          : "An unexpected error occurred.";
      setApiMessage({
        type: "danger",
        message: `Submission failed: ${errorMessage}`,
      });
      setOpen(true);
      setApiMessage({
        type: "danger",
        message: `Submission failed: ${errorMessage}`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderSearchableFields = () => {
    // Determine the actual country object being used for searching (University, Institute, Course)
    // It's either the selected preferred country (if not 'Other') or the country from the full list.
    const currentCountryForSearch =
      formData.preferredCountry?.value !== "Other"
        ? {
            value: formData.preferredCountry?.countryId,
            label: formData.preferredCountry?.label,
          }
        : formData.country;

    const isUniDisabled = !currentCountryForSearch && !useManualUniversity;

    const isInstDisabled =
      (!currentCountryForSearch ||
        (!formData.university && !useManualUniversity)) &&
      !useManualInstitute;

    const isCourseDisabled =
      (!currentCountryForSearch ||
        (!formData.university && !useManualUniversity) ||
        (!formData.institute && !useManualInstitute)) &&
      !useManualCourse;

    // Determine the countryId to pass to fetchDropdownData
    const countryIdForSearch = currentCountryForSearch?.value;

    return (
      <div id="conditionalFields" className="classConditionalFields">
        {/*
        // ✅ START: Country of Study Dropdown - Hidden by default, shown if "Other" is selected in Preferred Study Place 
        // This is necessary because in Confirmed/Applied status, this entire block is rendered, 
        // and if "Other" is selected, the Country of Study field must appear here.
        */}
        {formData.preferredCountry?.value === "Other" && (
          <Row>
            <Form.Group as={Col} xs={12} sm={6} md={6} className="marCol">
              <Form.Label className="form-label">
                Country of Study <span style={{ color: "red" }}>*</span>
              </Form.Label>
              <Autocomplete
                PopperComponent={CustomPopper}
                options={countries}
                className="avance-autocomplete-style"
                getOptionLabel={(option) => option.label}
                onChange={handleCountryChange}
                value={formData.country}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label=""
                    placeholder="Select country"
                    error={!!errors.country}
                  />
                )}
              />
              {errors.country && (
                <Form.Control.Feedback
                  type="invalid"
                  style={{ display: "block" }}
                >
                  {errors.country}
                </Form.Control.Feedback>
              )}
            </Form.Group>
            <Col xs={12} sm={6} md={6} className="marCol"></Col>{" "}
            {/* Empty column to maintain layout */}
          </Row>
        )}
        {/* ✅ END: Country of Study Dropdown (Only visible if 'Other' is selected) */}

        <Row>
          <Form.Group as={Col} xs={12} sm={6} md={6} className="marCol">
            <Form.Label className="form-label">
              University Name <span style={{ color: "red" }}>*</span>
            </Form.Label>
            <Grid
              container
              justifyContent="flex-end"
              alignItems="center"
              style={{ float: "right" }}
            >
              <Grid item>
                <Link
                  component="button"
                  variant="body2"
                  onClick={(e) => {
                    e.preventDefault();
                    const nextMode = !useManualUniversity;
                    setUseManualUniversity(nextMode);
                    setFormData((prev) => ({
                      ...prev,
                      university: useManualUniversity ? null : "",
                    }));
                    if (nextMode) {
                      setUseManualInstitute(true);
                      setUseManualCourse(true);
                      setFormData((prev) => ({
                        ...prev,
                        institute: "",
                        course: "",
                      }));
                    } else {
                      // ✅ Switching back to dropdown mode
                      setUseManualInstitute(false);
                      setUseManualCourse(false);
                      setFormData((prev) => ({
                        ...prev,
                        institute: null,
                        course: null,
                      }));
                    }
                  }}
                >
                  {useManualUniversity ? "Select From List" : "Not in list?"}
                </Link>
              </Grid>
            </Grid>
            {useManualUniversity ? (
              <>
                <Form.Control
                  name="university"
                  value={formData.university || ""}
                  onChange={handleChange}
                  isInvalid={!!errors.university}
                  placeholder="University Name"
                  disabled={isUniDisabled}
                  className="custom-autocomplete-input"
                />
                {errors.university && (
                  <Form.Control.Feedback
                    type="invalid"
                    style={{ display: "block" }}
                  >
                    {errors.university}
                  </Form.Control.Feedback>
                )}
              </>
            ) : (
              <>
                <Autocomplete
                  options={universities}
                  PopperComponent={CustomPopper}
                  disableClearable
                  className="avance-autocomplete-style"
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  onChange={handleUniversityChange}
                  onInputChange={(e, inputValue) => {
                    if (inputValue.length >= 4 && currentCountryForSearch) {
                      fetchDropdownData(
                        "university",
                        inputValue,
                        countryIdForSearch
                      ).then(setUniversities);
                    } else {
                      setUniversities([]);
                    }
                  }}
                  value={formData.university}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label=""
                      placeholder="Select University"
                      error={!!errors.university}
                    />
                  )}
                  disabled={isUniDisabled}
                />
                {errors.university && (
                  <Form.Control.Feedback
                    type="invalid"
                    style={{ display: "block" }}
                  >
                    {errors.university}
                  </Form.Control.Feedback>
                )}
              </>
            )}
          </Form.Group>

          <Form.Group as={Col} xs={12} sm={6} md={6} className="marCol">
            <Form.Label className="form-label">
              Institute Name <span style={{ color: "red" }}>*</span>
            </Form.Label>
            <Grid
              container
              justifyContent="flex-end"
              alignItems="center"
              style={{ float: "right" }}
            >
              <Grid item>
                <Link
                  component="button"
                  variant="body2"
                  onClick={(e) => {
                    e.preventDefault();
                    const nextMode = !useManualInstitute;
                    setUseManualInstitute(nextMode);
                    setFormData((prev) => ({
                      ...prev,
                      institute: nextMode ? "" : null,
                    }));

                    if (nextMode) {
                      setUseManualCourse(true);
                      setFormData((prev) => ({
                        ...prev,
                        course: "",
                      }));
                    } else {
                      setUseManualCourse(false);
                      setFormData((prev) => ({
                        ...prev,
                        course: null,
                      }));
                    }
                  }}
                >
                  {useManualInstitute ? "Select From List" : "Not in list?"}
                </Link>
              </Grid>
            </Grid>

            {useManualInstitute ? (
              <>
                <Form.Control
                  name="institute"
                  value={formData.institute || ""}
                  onChange={handleChange}
                  isInvalid={!!errors.institute}
                  placeholder="Institute Name"
                  disabled={isInstDisabled}
                  className="custom-autocomplete-input"
                />
                {errors.institute && (
                  <Form.Control.Feedback
                    type="invalid"
                    style={{ display: "block" }}
                  >
                    {errors.institute}
                  </Form.Control.Feedback>
                )}
              </>
            ) : (
              <>
                <Autocomplete
                  options={institutes}
                  disableClearable
                  className="avance-autocomplete-style"
                  PopperComponent={CustomPopper}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  onChange={handleInstituteChange}
                  onInputChange={(e, inputValue) => {
                    if (
                      inputValue.length >= 4 &&
                      currentCountryForSearch &&
                      formData.university
                    ) {
                      fetchDropdownData(
                        "institute",
                        inputValue,
                        countryIdForSearch,
                        formData.university.value
                      ).then(setInstitutes);
                    } else {
                      setInstitutes([]);
                    }
                  }}
                  value={formData.institute}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label=""
                      placeholder="Select Institute"
                      error={!!errors.institute}
                    />
                  )}
                  disabled={isInstDisabled}
                />
                {errors.institute && (
                  <Form.Control.Feedback
                    type="invalid"
                    style={{ display: "block" }}
                  >
                    {errors.institute}
                  </Form.Control.Feedback>
                )}
              </>
            )}
          </Form.Group>
        </Row>
        <Row>
          <Form.Group as={Col} xs={12} sm={6} md={6} className="marCol">
            <Form.Label className="form-label">
              Course Name <span style={{ color: "red" }}>*</span>
            </Form.Label>
            <Grid
              container
              justifyContent="flex-end"
              alignItems="center"
              style={{ float: "right" }}
            >
              <Grid item>
                <Link
                  component="button"
                  variant="body2"
                  onClick={(e) => {
                    e.preventDefault();
                    setUseManualCourse(!useManualCourse);
                    setFormData((prev) => ({
                      ...prev,
                      course: useManualCourse ? null : "",
                    }));
                  }}
                >
                  {useManualCourse ? "Select From List" : "Not in list?"}
                </Link>
              </Grid>
            </Grid>
            {useManualCourse ? (
              <>
                <Form.Control
                  name="course"
                  value={formData.course || ""}
                  onChange={handleChange}
                  isInvalid={!!errors.course}
                  placeholder="Course Name"
                  disabled={isCourseDisabled}
                  className="custom-autocomplete-input"
                />
                {errors.course && (
                  <Form.Control.Feedback
                    type="invalid"
                    style={{ display: "block" }}
                  >
                    {errors.course}
                  </Form.Control.Feedback>
                )}
              </>
            ) : (
              <>
                <Autocomplete
                  disableClearable
                  className="avance-autocomplete-style"
                  options={courses}
                  PopperComponent={CustomPopper}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  onChange={handleCourseChange}
                  onInputChange={(e, inputValue) => {
                    if (inputValue.length >= 4 && formData.institute) {
                      fetchDropdownData(
                        "course",
                        inputValue,
                        countryIdForSearch,
                        formData.university?.value,
                        formData.institute.value,
                        formData.courseCategory === "Graduation" ? true : false
                      ).then(setCourses);
                    } else {
                      setCourses([]);
                    }
                  }}
                  value={formData.course}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label=""
                      placeholder="Select Course"
                      error={!!errors.course}
                    />
                  )}
                  disabled={isCourseDisabled}
                />
                {errors.course && (
                  <Form.Control.Feedback
                    type="invalid"
                    style={{ display: "block" }}
                  >
                    {errors.course}
                  </Form.Control.Feedback>
                )}
              </>
            )}
          </Form.Group>
        </Row>
      </div>
    );
  };

  const renderStep1 = () => (
    <>
      <Row className="marBottom">
        {/* First Name */}
        <Col xs={12} sm={6} md={6} className="marCol">
          <Form.Group>
            {/* REMOVED: Form.Label */}
            <Form.Control
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              isInvalid={!!errors.firstName}
              className="form-control avance-input"
              placeholder="First Name *" // ADDED Placeholder
            />
            <Form.Control.Feedback type="invalid" className="invalid-feedback">
              {errors.firstName}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        {/* Last Name (Placeholder modified to "First Name" per image) */}
        <Col xs={12} sm={6} md={6} className="marCol">
          <Form.Group>
            {/* REMOVED: Form.Label */}
            <Form.Control
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              isInvalid={!!errors.lastName}
              className="form-control avance-input"
              placeholder="Last Name *" // ADDED Placeholder (Likely Last Name in your logic)
            />
            <Form.Control.Feedback type="invalid" className="invalid-feedback">
              {errors.lastName}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <Row className="marBottom">
        {/* Mobile No. */}
        <Col xs={12} sm={6} md={6} className="marCol">
          <Form.Group>
            {/* REMOVED: Form.Label */}
            <Form.Control
              type="number"
              ref={setInputRef}
              onWheel={handleWheel}
              style={{ appearance: "textfield" }}
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              onInput={(e) => {
                e.target.value = e.target.value.slice(0, 10);
              }}
              isInvalid={!!errors.mobile}
              className="form-control avance-input"
              placeholder="Mobile No. *" // ADDED Placeholder
            />
            <Form.Control.Feedback type="invalid" className="invalid-feedback">
              {errors.mobile}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        {/* Email ID */}
        <Col xs={12} sm={6} md={6} className="marCol">
          {" "}
          <Form.Group>
            {/* REMOVED: Form.Label */}
            <Form.Control
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              isInvalid={!!errors.email}
              className="form-control avance-input"
              placeholder="Email ID *" // ADDED Placeholder
            />
            <Form.Control.Feedback type="invalid" className="invalid-feedback">
              {errors.email}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <Row className="marBottom">
        {/* IDP Branch (Select) */}
        <Col xs={12} sm={6} md={6} className="marCol">
          {" "}
          <Form.Group>
            {/* REMOVED: Form.Label */}
            <Form.Select
              name="idpRepresentative"
              value={formData.idpRepresentative}
              onChange={handleChange}
              isInvalid={!!errors.idpRepresentative}
              className="form-select avance-input"
            >
              <option value="" disabled hidden>
                IDP Branch *
              </option>{" "}
              {/* ADDED Placeholder option */}
              {idpRepresentatives.map((rep) => (
                <option key={rep.userId} value={rep.userId}>
                  {rep.name}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid" className="invalid-feedback">
              {errors.idpRepresentative}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        {/* IDP Counsellor */}
        <Col xs={12} sm={6} md={6} className="marCol">
          {" "}
          <Form.Group>
            {/* REMOVED: Form.Label */}
            <Form.Control
              type="text"
              name="counsellor"
              value={formData.counsellor}
              onChange={handleChange}
              isInvalid={!!errors.counsellor}
              className="form-control avance-input"
              placeholder="IDP Counsellor *" // ADDED Placeholder
              maxLength={50}
            />
            <Form.Control.Feedback type="invalid" className="invalid-feedback">
              {errors.counsellor}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <Row className="marBottom">
        {/* Customer Pincode */}
        <Col xs={12} sm={6} md={6} className="marCol">
          {" "}
          <Form.Group>
            {/* REMOVED: Form.Label */}
            <Form.Control
              ref={setInputRef}
              type="number"
              onWheel={handleWheel}
              name="pincode"
              style={{ appearance: "textfield" }}
              value={formData.pincode}
              onChange={handleChange}
              onInput={(e) => {
                e.target.value = e.target.value.slice(0, 6);
              }}
              isInvalid={!!errors.pincode}
              className="form-control avance-input"
              placeholder="Customer Pincode *" // ADDED Placeholder
            />
            <Form.Control.Feedback type="invalid" className="invalid-feedback">
              {errors.pincode}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        {/* Admission Status (Select) */}
        <Col xs={12} sm={6} md={6} className="marCol">
          {" "}
          <Form.Group>
            {/* REMOVED: Form.Label */}
            <Form.Select
              name="admissionStatus"
              value={formData.admissionStatus}
              onChange={handleChange}
              isInvalid={!!errors.admissionStatus}
              className="form-select avance-input"
            >
              <option value="" disabled hidden>
                Admission Status *
              </option>
              {/* ADDED Placeholder option */}
              <option value="Confirmed">Confirmed</option>
              <option value="Applied">Applied</option>
              <option value="Undecided">Undecided</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid" className="invalid-feedback">
              {errors.admissionStatus}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
      <div className="d-flex justify-content-center mt-4 submit-button">
        <Button onClick={handleNextStep} className="c-button">
          Next
        </Button>
      </div>
    </>
  );

  const renderStep2 = () => (
    <>
      <Row>
        <Form.Group as={Col} xs={12} sm={6} md={6} className="marCol">
          <Form.Label className="form-label">
            Stream <span style={{ color: "red" }}>*</span>
          </Form.Label>
          <Form.Select
            name="stream"
            value={formData.stream}
            onChange={handleChange}
            isInvalid={!!errors.stream}
            className="form-select avance-input"
          >
            <option value="" disabled hidden>
              Stream *
            </option>
            <option value="STEM">STEM</option>
            <option value="MANAGEMENT">MANAGEMENT</option>
            <option value="OTHERS">OTHERS</option>
          </Form.Select>
          <Form.Control.Feedback type="invalid" className="error-message">
            {errors.stream}
          </Form.Control.Feedback>
        </Form.Group>
        <Form.Group as={Col} xs={12} sm={6} md={6} className="marCol">
          <Form.Label className="form-label">
            Course Category <span style={{ color: "red" }}>*</span>
          </Form.Label>
          <Form.Select
            name="courseCategory"
            value={formData.courseCategory}
            onChange={handleChange}
            isInvalid={!!errors.courseCategory}
            className="form-select avance-input"
          >
            <option value="" disabled hidden>
              Course Category *
            </option>{" "}
            <option value="Graduation">Under Graduation</option>
            <option value="Post Graduation">Post Graduation</option>
          </Form.Select>
          <Form.Control.Feedback type="invalid" className="error-message">
            {errors.courseCategory}
          </Form.Control.Feedback>
        </Form.Group>
      </Row>
      <Row>
        <Form.Group as={Col} xs={12} sm={6} md={6} className="marCol">
          <Form.Label className="form-label">
            Loan Amount <span style={{ color: "red" }}>*</span>
          </Form.Label>
          <Form.Select
            name="loanAmount"
            value={formData.loanAmount}
            onChange={handleChange}
            isInvalid={!!errors.loanAmount}
            className="form-select avance-input"
          >
            <option value="" disabled hidden>
              Loan Amount *
            </option>{" "}
            <option value="1500000">10 Lacs to 20 Lacs</option>
            <option value="3500000">20 Lacs to 50 Lacs</option>
            <option value="6000000">Above 50 Lacs</option>
          </Form.Select>
          <Form.Control.Feedback type="invalid" className="error-message">
            {errors.loanAmount}
          </Form.Control.Feedback>
        </Form.Group>
        {/* New Preferred Study Place Dropdown - Replaces the original Country of Study field */}
        <Form.Group as={Col} xs={12} sm={6} md={6} className="marCol">
          <Form.Label className="form-label">
            Preferred Study Place <span style={{ color: "red" }}>*</span>
          </Form.Label>
          <Autocomplete
            className="avance-autocomplete-style"
            PopperComponent={CustomPopper}
            options={PREFERRED_COUNTRIES_OPTIONS}
            getOptionLabel={(option) => option.label}
            onChange={handlePreferredCountryChange}
            value={formData.preferredCountry}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label=""
                placeholder="Preferred Study Place *"
                error={!!errors.preferredCountry}
              />
            )}
          />
          {errors.preferredCountry && (
            <Form.Control.Feedback type="invalid" style={{ display: "block" }}>
              {errors.preferredCountry}
            </Form.Control.Feedback>
          )}
        </Form.Group>
        {/* Original Country of Study Dropdown - HIDE/REMOVE it from here. The logic to show it when 'Other' is selected 
            for Undecided is below, and for Confirmed/Applied is within renderSearchableFields. */}

        {/* This block handles the Country of Study when Admission Status is Undecided and Preferred is "Other" */}
        {formData.admissionStatus === "Undecided" &&
          formData.preferredCountry?.value === "Other" && (
            <Row>
              <Form.Group as={Col} xs={12} sm={6} md={6} className="marCol">
                <Form.Label className="form-label">
                  Country of Study <span style={{ color: "red" }}>*</span>
                </Form.Label>
                <Autocomplete
                  className="avance-autocomplete-style"
                  PopperComponent={CustomPopper}
                  options={countries}
                  getOptionLabel={(option) => option.label}
                  onChange={handleCountryChange}
                  value={formData.country}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label=""
                      placeholder="Select country"
                      error={!!errors.country}
                    />
                  )}
                />
                {errors.country && (
                  <Form.Control.Feedback
                    type="invalid"
                    style={{ display: "block" }}
                  >
                    {errors.country}
                  </Form.Control.Feedback>
                )}
              </Form.Group>
              <Col xs={12} sm={6} md={6} className="marCol"></Col>{" "}
              {/* Empty column to maintain layout */}
            </Row>
          )}
      </Row>

      {/* // The original country dropdown slot for Undecided/Confirmed/Applied was here in the code.
      // Now, the Preferred dropdown is in that spot.
      // The original country dropdown only appears above if Preferred is "Other" 
      // in the Undecided block, or inside renderSearchableFields for Confirmed/Applied.
      */}

      {(formData.admissionStatus === "Confirmed" ||
        formData.admissionStatus === "Applied") &&
        renderSearchableFields()}

      {formData.admissionStatus === "Undecided" && (
        <div id="conditionalFields">
          {/* Academic fields for Undecided */}
          <Row>
            <Form.Group as={Col} xs={12} sm={6} md={6} className="marCol">
              <Form.Label className="form-label">12th Marks in (%)</Form.Label>
              <Form.Control
                ref={setInputRef}
                type="number"
                // REMOVE this --> onWheel={handleWheel}
                style={{ appearance: "textfield" }}
                placeholder="12th Marks in (%)"
                name="marks12th"
                value={formData.marks12th}
                onChange={handleChange}
                isInvalid={!!errors.academicScores}
                className="form-control avance-input"
              />
            </Form.Group>
            <Form.Group as={Col} xs={12} sm={6} md={6} className="marCol">
              <Form.Label className="form-label"></Form.Label>
              <Form.Control
                ref={setInputRef}
                placeholder="  Graduation GPA in (%)"
                type="number"
                // REMOVE this --> onWheel={handleWheel}
                style={{ appearance: "textfield" }}
                name="graduationGPA"
                value={formData.graduationGPA}
                onChange={handleChange}
                isInvalid={!!errors.academicScores}
                className="form-control avance-input"
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group as={Col} xs={12} sm={6} md={6} className="marCol">
              <Form.Label className="form-label">GRE Score</Form.Label>
              <Form.Control
                ref={setInputRef}
                type="number"
                // REMOVE this --> onWheel={handleWheel}
                style={{ appearance: "textfield" }}
                name="greScore"
                value={formData.greScore}
                onChange={handleChange}
                placeholder="GRE Score"
                isInvalid={!!errors.academicScores}
                className="form-control avance-input"
              />
            </Form.Group>
            <Form.Group as={Col} xs={12} sm={6} md={6} className="marCol">
              <Form.Label className="form-label">GMAT Score</Form.Label>
              <Form.Control
                ref={setInputRef}
                type="number"
                // REMOVE this --> onWheel={handleWheel}
                style={{ appearance: "textfield" }}
                name="gmatScore"
                placeholder="GMAT Score"
                value={formData.gmatScore}
                onChange={handleChange}
                isInvalid={!!errors.academicScores}
                className="form-control avance-input"
              />
            </Form.Group>
          </Row>
          {errors.academicScores && (
            <Alert
              variant="danger"
              className="err-msg text-center"
              dismissible
              onClose={() =>
                setErrors((prev) => ({ ...prev, academicScores: "" }))
              }
            >
              {errors.academicScores}
            </Alert>
          )}
        </div>
      )}

      <div className="d-flex justify-content-center mt-4">
        <Button
          onClick={() => setCurrentStep(1)}
          className="avance-btn-next"
          style={{ marginRight: "10px" }}
        >
          Previous
        </Button>
        <Button type="submit" className="avance-btn-next" disabled={isLoading}>
          {isLoading ? (
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
            />
          ) : (
            "Submit"
          )}
        </Button>
      </div>
    </>
  );

  return (
    <div className="cyan-bg pt-0 loan-banner position-relative loan-apply-banner">
      <div className="d-flex align-items-center banner banner-back">
        <Container fluid className="text-white">
          <Row className="align-items-start">
            <Col lg={1} className="d-flex p-0">
              <div className="bg-decor-circle-lg-tl"></div>
              <div className="bg-decor-dots-bl-float"></div>
              <div className="bg-decor-teal-dot-left"></div>
            </Col>
            <Col lg={6} className="d-flex p-0">
              <div className="rectangle">
                <h2 className="loan-heading">
                  Let Your Study Abroad Dreams Take Flight with an{" "}
                  <span className="c_dark">Avanse Education Loan!</span>
                </h2>
                <ul className="loan-list d-none d-md-block">
                  <li>Loan Amount Ranging From Rs. 10 Lacs to Rs. 1.5Cr.*</li>
                  <li>Attractive student loan interest rates</li>
                  <li>100% finance for course fee and other expenses</li>
                  <li>Sanction within 3 days*</li>
                </ul>
                <span className="loan-tc d-none d-md-block">
                  *T&amp;C Apply
                </span>
              </div>
            </Col>
            <Col md={4} className="ml-0 form-background">
              <div className="apply-now-form py-3" id="myForm">
                <span className="form-heading d-block text-center">
                  Help us connect with you
                </span>
                <Form onSubmit={handleSubmit}>
                  {currentStep === 1 ? renderStep1() : renderStep2()}
                </Form>
              </div>
            </Col>
            <Col lg={1} className="d-flex p-0">
              <div className="bg-decor-dots-tr"></div>
              <div className="bg-decor-teal-dot-right"></div>
              <div className="bg-decor-teal-dot-right-small"></div>
              <div className="bg-decor-dots-tr-b"></div>
              <div className="bg-decor-circle-lg-br-ring"></div>
            </Col>
          </Row>
        </Container>
      </div>

      <ThankYouModalComponent
        show={showThankYouModal}
        onHide={() => {
          setShowThankYouModal(false);
          window.location.reload();
        }}
        breData={breData}
      />
      {apiMessage.message && (
        <div
          className="alert-message-cust"
          style={{
            position: "fixed",
            top: 5,
            left: 0,
            right: 0,
            zIndex: 9999,
          }}
        >
          <Alert
            variant={apiMessage.type}
            className="err-msg text-center"
            dismissible
            onClose={() => setApiMessage({ type: "", message: "" })}
          >
            {apiMessage.message}
          </Alert>
        </div>
      )}
    </div>
  );
};

export default LoanForm;
